import React, { useState, useEffect } from "react";

const API = "https://canada-billing.ameontech.com";

const PROVINCES = ["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"];
const CLIENT_TYPES = ["Business", "Individual", "Government", "Non-Profit"];
const INDUSTRIES = [
  "Technology & IT", "Consulting & Professional Services", "Construction & Trades",
  "Healthcare & Medical", "Retail & E-Commerce", "Finance & Accounting",
  "Legal Services", "Real Estate", "Manufacturing", "Transportation & Logistics",
  "Education & Training", "Media & Marketing", "Food & Hospitality", "Other"
];

const inp = {
  width: "100%", padding: "9px 12px", borderRadius: 8,
  border: "1.5px solid #e2e8f0", fontSize: 14, color: "#2d3748",
  outline: "none", boxSizing: "border-box", background: "#fafbfc",
};
const labelStyle = { fontSize: 13, fontWeight: 600, color: "#2d3748", display: "block", marginBottom: 4 };
const subStyle   = { fontSize: 12, color: "#a0aec0", display: "block", marginBottom: 4 };

const EMPTY = {
  client_name: "", client_type: "Business", industry: "", status: "Active",
  client_since: new Date().toISOString().split("T")[0],
  email: "", mobile: "", gst_number: "", notes: "",
  address_line1: "", address_line2: "", city: "Toronto", province: "ON",
  postal_code: "", country: "Canada",
  billing_address_same_as_primary: true,
  billing_address_line1: "", billing_address_line2: "",
  billing_city: "", billing_province: "ON",
  billing_postal_code: "", billing_country: "Canada",
};

function Field({ label, sub, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <label style={labelStyle}>{label}</label>
      {sub && <span style={subStyle}>{sub}</span>}
      {children}
    </div>
  );
}

function Row2({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>{children}</div>;
}

function SectionHead({ icon, title }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "18px 0 10px", borderBottom: "1.5px solid #f0f0f0", paddingBottom: 8 }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <span style={{ fontWeight: 700, fontSize: 14, color: "#2d3748" }}>{title}</span>
    </div>
  );
}

// ── Modal ──────────────────────────────────────────────
function ClientModal({ client, onClose, onSave }) {
  const [form, setForm]     = useState(client ? { ...client } : { ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");
  const token   = localStorage.getItem("token");
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const setCheck = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.checked }));

  const handleSave = async () => {
    if (!form.client_name.trim()) return setError("Client name is required.");
    setSaving(true); setError("");
    try {
      const url    = client ? `${API}/api/clients/${client.id}` : `${API}/api/clients`;
      const method = client ? "PUT" : "POST";
      const res    = await fetch(url, { method, headers, body: JSON.stringify(form) });
      const data   = await res.json();
      if (!res.ok) throw new Error(data.message);
      onSave(data.client);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, width: "min(680px, 96vw)",
        maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
        display: "flex", flexDirection: "column"
      }}>
        {/* Header */}
        <div style={{
          padding: "20px 24px 16px", borderBottom: "1.5px solid #f0f0f0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          position: "sticky", top: 0, background: "#fff", borderRadius: "16px 16px 0 0", zIndex: 2
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(79,142,247,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
              {client ? "✏️" : "➕"}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: "#1a202c" }}>{client ? "Edit Client" : "Add New Client"}</div>
              <div style={{ fontSize: 12, color: "#a0aec0" }}>{client ? "Update client information" : "Fill in client details"}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "none", fontSize: 22, cursor: "pointer", color: "#a0aec0", lineHeight: 1 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 10 }}>

          <SectionHead icon="👤" title="Basic Information" />
          <Field label="Client Name *">
            <input style={inp} value={form.client_name} onChange={set("client_name")} placeholder="Acme Corp" />
          </Field>
          <Row2>
            <Field label="Client Type">
              <select style={inp} value={form.client_type} onChange={set("client_type")}>
                {CLIENT_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select style={inp} value={form.status} onChange={set("status")}>
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </Field>
          </Row2>
          <Row2>
            <Field label="Industry">
              <select style={inp} value={form.industry} onChange={set("industry")}>
                <option value="">-- Select --</option>
                {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
              </select>
            </Field>
            <Field label="Client Since">
              <input style={inp} type="date" value={form.client_since} onChange={set("client_since")} />
            </Field>
          </Row2>

          <SectionHead icon="📞" title="Contact" />
          <Row2>
            <Field label="Email">
              <input style={inp} type="email" value={form.email} onChange={set("email")} placeholder="info@acme.ca" />
            </Field>
            <Field label="Mobile">
              <input style={inp} value={form.mobile} onChange={set("mobile")} placeholder="416-555-0100" />
            </Field>
          </Row2>
          <Field label="GST/HST Number" sub="e.g. 123456789 RT0001">
            <input style={inp} value={form.gst_number} onChange={set("gst_number")} placeholder="123456789 RT0001" />
          </Field>

          <SectionHead icon="📍" title="Primary Address" />
          <Field label="Address Line 1">
            <input style={inp} value={form.address_line1} onChange={set("address_line1")} placeholder="123 King St W" />
          </Field>
          <Field label="Address Line 2">
            <input style={inp} value={form.address_line2} onChange={set("address_line2")} placeholder="Suite 200" />
          </Field>
          <Row2>
            <Field label="City">
              <input style={inp} value={form.city} onChange={set("city")} placeholder="Toronto" />
            </Field>
            <Field label="Province">
              <select style={inp} value={form.province} onChange={set("province")}>
                <option value="">-- Select --</option>
                {PROVINCES.map(p => <option key={p}>{p}</option>)}
              </select>
            </Field>
          </Row2>
          <Row2>
            <Field label="Postal Code">
              <input style={inp} value={form.postal_code} onChange={set("postal_code")} placeholder="M5V 1A1" />
            </Field>
            <Field label="Country">
              <input style={inp} value={form.country} onChange={set("country")} placeholder="Canada" />
            </Field>
          </Row2>

          <SectionHead icon="🏦" title="Billing Address" />
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14, color: "#2d3748" }}>
            <input type="checkbox" checked={form.billing_address_same_as_primary} onChange={setCheck("billing_address_same_as_primary")} />
            Same as Primary Address
          </label>

          {!form.billing_address_same_as_primary && (
            <>
              <Field label="Billing Address Line 1">
                <input style={inp} value={form.billing_address_line1} onChange={set("billing_address_line1")} placeholder="456 Bay St" />
              </Field>
              <Field label="Billing Address Line 2">
                <input style={inp} value={form.billing_address_line2} onChange={set("billing_address_line2")} placeholder="Floor 10" />
              </Field>
              <Row2>
                <Field label="City">
                  <input style={inp} value={form.billing_city} onChange={set("billing_city")} placeholder="Toronto" />
                </Field>
                <Field label="Province">
                  <select style={inp} value={form.billing_province} onChange={set("billing_province")}>
                    <option value="">-- Select --</option>
                    {PROVINCES.map(p => <option key={p}>{p}</option>)}
                  </select>
                </Field>
              </Row2>
              <Row2>
                <Field label="Postal Code">
                  <input style={inp} value={form.billing_postal_code} onChange={set("billing_postal_code")} placeholder="M5H 2N2" />
                </Field>
                <Field label="Country">
                  <input style={inp} value={form.billing_country} onChange={set("billing_country")} placeholder="Canada" />
                </Field>
              </Row2>
            </>
          )}

          <SectionHead icon="📝" title="Notes" />
          <Field label="Notes">
            <textarea style={{ ...inp, resize: "vertical" }} rows={3}
              value={form.notes} onChange={set("notes")} placeholder="Any additional notes..." />
          </Field>

          {error && (
            <div style={{ padding: "10px 14px", background: "#fff5f5", border: "1px solid #feb2b2", borderRadius: 8, color: "#c53030", fontSize: 13 }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "14px 24px", borderTop: "1.5px solid #f0f0f0",
          display: "flex", justifyContent: "flex-end", gap: 10,
          position: "sticky", bottom: 0, background: "#fff", borderRadius: "0 0 16px 16px"
        }}>
          <button onClick={onClose} style={{
            padding: "9px 20px", border: "1.5px solid #e2e8f0", borderRadius: 8,
            background: "#fff", color: "#718096", fontSize: 14, cursor: "pointer", fontWeight: 600
          }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{
            padding: "9px 24px", border: "none", borderRadius: 8,
            background: saving ? "#a0aec0" : "#4f8ef7", color: "#fff",
            fontSize: 14, cursor: saving ? "not-allowed" : "pointer", fontWeight: 600
          }}>
            {saving ? "Saving..." : client ? "Update Client" : "Add Client"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Delete Confirm ─────────────────────────────────────
function DeleteModal({ client, onClose, onDelete }) {
  const [loading, setLoading] = useState(false);
  const token   = localStorage.getItem("token");

  const handleDelete = async () => {
    setLoading(true);
    try {
      await fetch(`${API}/api/clients/${client.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      onDelete(client.id);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100 }}>
      <div style={{ background: "#fff", borderRadius: 14, padding: 28, width: 360, boxShadow: "0 10px 40px rgba(0,0,0,0.15)", textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 10 }}>🗑️</div>
        <div style={{ fontWeight: 700, fontSize: 16, color: "#1a202c", marginBottom: 8 }}>Delete Client?</div>
        <div style={{ fontSize: 14, color: "#718096", marginBottom: 22 }}>
          Are you sure you want to delete <strong>{client.client_name}</strong>? This action cannot be undone.
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <button onClick={onClose} style={{ padding: "9px 22px", border: "1.5px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#718096", fontSize: 14, cursor: "pointer", fontWeight: 600 }}>Cancel</button>
          <button onClick={handleDelete} disabled={loading} style={{ padding: "9px 22px", border: "none", borderRadius: 8, background: "#e53e3e", color: "#fff", fontSize: 14, cursor: "pointer", fontWeight: 600 }}>
            {loading ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Clients Page ──────────────────────────────────
export default function Customermanagementdata() {
  const [clients, setClients]     = useState([]);
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType]     = useState("");
  const [modal, setModal]         = useState(null); // null | "add" | {client}
  const [deleteModal, setDeleteModal]   = useState(null);
  const token   = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchClients = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search)       params.set("search", search);
      if (filterStatus) params.set("status", filterStatus);
      if (filterType)   params.set("type", filterType);
      const [cRes, sRes] = await Promise.all([
        fetch(`${API}/api/clients?${params}`, { headers }),
        fetch(`${API}/api/clients/stats`, { headers }),
      ]);
      const cData = await cRes.json();
      const sData = await sRes.json();
      setClients(cData.clients || []);
      setStats(sData.stats || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, [search, filterStatus, filterType]);

  const handleSave = (client) => {
    setClients(prev => {
      const exists = prev.find(c => c.id === client.id);
      return exists ? prev.map(c => c.id === client.id ? client : c) : [client, ...prev];
    });
    setModal(null);
    fetchClients(); // refresh stats
  };

  const handleDelete = (id) => {
    setClients(prev => prev.filter(c => c.id !== id));
    setDeleteModal(null);
    fetchClients();
  };

  const statusColor = (s) => s === "Active"
    ? { background: "#f0fff4", color: "#276749", border: "1px solid #9ae6b4" }
    : { background: "#fff5f5", color: "#c53030", border: "1px solid #feb2b2" };

  const typeIcon = (t) => ({ Business: "🏢", Individual: "👤", Government: "🏛️", "Non-Profit": "🤝" }[t] || "🏢");

  const fmtCurrency = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2 })}`;

  return (
    <div style={{ padding: "0 0 40px" }}>

      {/* ── Stats Row ── */}
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 24 }}>
          {[
            { label: "Total Clients",    value: stats.total,              icon: "👥", color: "#4f8ef7" },
            { label: "Active",           value: stats.active,             icon: "✅", color: "#48bb78" },
            { label: "Total Revenue",    value: fmtCurrency(stats.total_revenue), icon: "💰", color: "#ed8936" },
            { label: "Outstanding",      value: fmtCurrency(stats.outstanding_amount), icon: "⏳", color: "#e53e3e" },
          ].map(s => (
            <div key={s.label} style={{
              background: "#fff", borderRadius: 12, padding: "16px 18px",
              border: "1.5px solid #f0f0f0", boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
            }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: "#a0aec0", marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Toolbar ── */}
      <div style={{
        background: "#fff", borderRadius: 12, padding: "14px 18px",
        border: "1.5px solid #f0f0f0", marginBottom: 16,
        display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center"
      }}>
        <input
          style={{ ...inp, flex: 1, minWidth: 180, maxWidth: 280 }}
          placeholder="🔍  Search clients..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select style={{ ...inp, width: "auto", minWidth: 120 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Status</option>
          <option>Active</option>
          <option>Inactive</option>
        </select>
        <select style={{ ...inp, width: "auto", minWidth: 140 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          {CLIENT_TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
        <div style={{ marginLeft: "auto" }}>
          <button onClick={() => setModal("add")} style={{
            padding: "9px 20px", background: "#4f8ef7", color: "#fff",
            border: "none", borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 6
          }}>
            ➕ Add Client
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1.5px solid #f0f0f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#a0aec0" }}>Loading clients...</div>
        ) : clients.length === 0 ? (
          <div style={{ padding: 60, textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>👥</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: "#2d3748" }}>No clients found</div>
            <div style={{ fontSize: 14, color: "#a0aec0", marginTop: 6 }}>Add your first client to get started</div>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1.5px solid #f0f0f0" }}>
                {["Client", "Type", "Contact", "City / Province", "Revenue", "Outstanding", "Status", "Actions"].map(h => (
                  <th key={h} style={{ padding: "12px 14px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#718096", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clients.map((c, i) => (
                <tr key={c.id} style={{ borderBottom: "1px solid #f7f7f7", background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                  <td style={{ padding: "12px 14px" }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#1a202c" }}>{c.client_name}</div>
                    {c.industry && <div style={{ fontSize: 12, color: "#a0aec0", marginTop: 2 }}>{c.industry}</div>}
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <span style={{ fontSize: 13, color: "#4a5568" }}>{typeIcon(c.client_type)} {c.client_type}</span>
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    {c.email  && <div style={{ fontSize: 13, color: "#4a5568" }}>✉️ {c.email}</div>}
                    {c.mobile && <div style={{ fontSize: 13, color: "#4a5568", marginTop: 2 }}>📱 {c.mobile}</div>}
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: 13, color: "#4a5568" }}>
                    {[c.city, c.province].filter(Boolean).join(", ") || "—"}
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 600, color: "#276749" }}>
                    {fmtCurrency(c.total_revenue)}
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 600, color: Number(c.outstanding_amount) > 0 ? "#c53030" : "#718096" }}>
                    {fmtCurrency(c.outstanding_amount)}
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, ...statusColor(c.status) }}>
                      {c.status}
                    </span>
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => setModal(c)} style={{
                        padding: "5px 12px", background: "#ebf4ff", color: "#2b6cb0",
                        border: "none", borderRadius: 6, fontSize: 12, cursor: "pointer", fontWeight: 600
                      }}>Edit</button>
                      <button onClick={() => setDeleteModal(c)} style={{
                        padding: "5px 12px", background: "#fff5f5", color: "#c53030",
                        border: "none", borderRadius: 6, fontSize: 12, cursor: "pointer", fontWeight: 600
                      }}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Count */}
      {!loading && clients.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 13, color: "#a0aec0", textAlign: "right" }}>
          Showing {clients.length} client{clients.length !== 1 ? "s" : ""}
        </div>
      )}

      {/* Modals */}
      {modal && (
        <ClientModal
          client={modal === "add" ? null : modal}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
      {deleteModal && (
        <DeleteModal
          client={deleteModal}
          onClose={() => setDeleteModal(null)}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}